import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Search as IcSearch, RefreshCw as IcRefresh, MailCheck as IcReplied, Mail as IcMail } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const PAGE_SIZE = 50;
const POLL_MS = 60000;

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString([], { day: 'numeric', month: 'short' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFullWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
         d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function MailSent() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback((off) => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(off));
    if (query) params.set('q', query);
    if (campaignFilter) params.set('campaign_id', campaignFilter);
    return isGlobalMode
      ? `${API_BASE}/admin/sent?${params.toString()}`
      : `${API_BASE}/clients/${clientId}/sent?${params.toString()}`;
  }, [query, campaignFilter, isGlobalMode, clientId]);

  const fetchList = useCallback(async ({ append = false, silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const off = append ? offset : 0;
      const res = await fetch(buildUrl(off), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load sent (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setHasMore(rows.length >= PAGE_SIZE);
      if (append) {
        setList(prev => [...prev, ...rows]);
        setOffset(off + rows.length);
      } else {
        setList(rows);
        setOffset(rows.length);
        if (firstLoadRef.current && rows.length) {
          firstLoadRef.current = false;
          setSelectedId(rows[0].id);
        }
      }
    } catch (e) {
      if (!silent) setListError(e.message);
    } finally {
      if (!silent) setListLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl, getToken, offset]);

  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    firstLoadRef.current = true;
    fetchList({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, query, campaignFilter]);

  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    pollRef.current = setInterval(() => {
      fetchList({ append: false, silent: true });
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, query, campaignFilter]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/sent/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load message (${res.status})`);
      const data = await res.json();
      setDetail(data);
    } catch (e) {
      setDetailError(e.message);
    } finally {
      setDetailLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const campaigns = useMemo(() => {
    const m = new Map();
    list.forEach(r => { if (r.campaign_id && r.campaign_name) m.set(r.campaign_id, r.campaign_name); });
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }));
  }, [list]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 6, overflow: 'hidden' }}>

      {/* LEFT RAIL */}
      <div style={{ width: 380, borderRight: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
        <div style={{ padding: 14, borderBottom: '1px solid #e5e5e5', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Sent</h2>
            <button onClick={() => fetchList({ append: false })} title="Refresh"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#666' }}>
              <IcRefresh size={15} />
            </button>
          </div>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <IcSearch size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#999' }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search subject, recipient, company…"
              style={{ width: '100%', padding: '7px 10px 7px 32px', fontSize: 13, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff', boxSizing: 'border-box' }} />
          </div>
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff' }}>
            <option value="">All campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {listLoading && list.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>Loading…</div>
          )}
          {listError && (
            <div style={{ padding: 14, color: '#b91c1c', fontSize: 12 }}>{listError}</div>
          )}
          {!listLoading && list.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#999', fontSize: 13 }}>
              <IcMail size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
              <div>No sent messages yet</div>
            </div>
          )}
          {list.map(r => {
            const isSelected = r.id === selectedId;
            return (
              <div key={r.id} onClick={() => setSelectedId(r.id)}
                style={{
                  padding: '12px 14px', borderBottom: '1px solid #ececec', cursor: 'pointer',
                  background: isSelected ? '#fff' : 'transparent', borderLeft: isSelected ? '3px solid #8B1A1A' : '3px solid transparent',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <div style={{ fontWeight: 500, fontSize: 13, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>
                    {r.contact_name || r.to_email}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>{formatWhen(r.sent_at)}</div>
                </div>
                <div style={{ fontSize: 13, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                  {r.subject || '(no subject)'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {r.has_reply && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#1a7f3c', color: '#fff', fontWeight: 500 }}>
                      <IcReplied size={10} />Replied
                    </span>
                  )}
                  {r.campaign_name && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#ececec', color: '#555' }}>
                      {r.campaign_name}{r.step_number ? ` · step ${r.step_number}` : ''}
                    </span>
                  )}
                  {r.company_name && (
                    <span style={{ fontSize: 10, color: '#888' }}>{r.company_name}</span>
                  )}
                </div>
              </div>
            );
          })}
          {hasMore && list.length >= PAGE_SIZE && (
            <button onClick={() => fetchList({ append: true })}
              style={{ width: '100%', padding: 10, background: 'transparent', border: 'none', borderTop: '1px solid #ececec', cursor: 'pointer', color: '#8B1A1A', fontSize: 12 }}>
              Load more
            </button>
          )}
        </div>
      </div>

      {/* RIGHT PANE */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selectedId && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
            Select a message to view
          </div>
        )}
        {selectedId && detailLoading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
            Loading…
          </div>
        )}
        {selectedId && detailError && (
          <div style={{ flex: 1, padding: 20, color: '#b91c1c', fontSize: 13 }}>{detailError}</div>
        )}
        {selectedId && detail && (
          <>
            <div style={{ padding: 16, borderBottom: '1px solid #e5e5e5', background: '#fff' }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: 16, fontWeight: 600 }}>{detail.subject || '(no subject)'}</h3>
              <div style={{ fontSize: 12, color: '#666', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <span><strong style={{ color: '#444' }}>To:</strong> {detail.contact_name ? `${detail.contact_name} <${detail.to_email}>` : detail.to_email}</span>
                {detail.company_name && <span><strong style={{ color: '#444' }}>Company:</strong> {detail.company_name}</span>}
                {detail.campaign_name && <span><strong style={{ color: '#444' }}>Campaign:</strong> {detail.campaign_name}{detail.step_number ? ` · step ${detail.step_number}` : ''}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>From: {detail.from_email}{detail.mailbox_email && detail.mailbox_email !== detail.from_email ? ` (via ${detail.mailbox_email})` : ''}</span>
                <span>Sent: {formatFullWhen(detail.sent_at)}</span>
                {detail.has_reply && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#1a7f3c', fontWeight: 500 }}>
                    <IcReplied size={11} />Reply received
                  </span>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: '#fafafa' }}>
              <div style={{ background: '#fff', padding: 18, borderRadius: 6, border: '1px solid #e5e5e5', fontSize: 14, lineHeight: 1.6, color: '#222' }}>
                {detail.body_html
                  ? <div dangerouslySetInnerHTML={{ __html: detail.body_html }} />
                  : <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>{detail.body_text || '(empty)'}</pre>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
